import React, { useCallback, useEffect, useRef } from 'react';

import styled from 'styled-components';

import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import SendIcon from '@grnhse/seedling/lib/birch/icons/send';
import Tokens from '@grnhse/seedling/lib/birch/tokens';

import { withLocalePrefix } from 'shared/utils/translation';
import { chatMessageTextStyles } from './message_styles';

const t = withLocalePrefix('shared.agent_chat');

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
  sendLabel?: string;
};

export function MessageInput({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder = t('placeholder'),
  sendLabel = t('send'),
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!disabled) {
        onSubmit();
      }
    }
  };

  return (
    <Container>
      <PromptRow>
        <Prompt>
          <StyledTextarea
            ref={textareaRef}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              onChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
          />
        </Prompt>
        <SubmitButton>
          <IconButton
            focusable
            icon={SendIcon}
            label={sendLabel}
            onClick={onSubmit}
            size="md"
            disabled={disabled}
            flat
          />
        </SubmitButton>
      </PromptRow>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Tokens.spacing['2']} ${Tokens.spacing['3']};
  border-top: 1px solid ${FullPalette.GREY_700};
`;

const PromptRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${Tokens.spacing['1']};
`;

const Prompt = styled.div`
  flex: 1;
  min-width: 0;
  background: ${FullPalette.WHITE};
  border: 1px solid ${FullPalette.GREY_700};
  border-radius: ${Tokens.spacing['0.5']};
  padding: ${Tokens.spacing['1']} ${Tokens.spacing['2']};
`;

const SubmitButton = styled.div`
  padding: ${Tokens.spacing['0.5']} 0;
`;

const StyledTextarea = styled.textarea`
  display: block;
  width: 100%;
  resize: none;
  overflow: hidden;
  border: none;
  outline: none;
  font-family: 'Untitled Sans', sans-serif;
  ${chatMessageTextStyles}
  max-height: 10.5rem;
  overflow-y: auto;
  padding: 0;
  box-sizing: border-box;

  &::placeholder {
    color: ${FullPalette.GREY_800};
  }
`;
